import { useEffect, useRef } from "react";
import { getSessionId, getAuthType } from "../api";
import * as RxClaimWebTools from "../utils/RxClaimWebTools";
import { getAIProgramUrl, getProgramUrl, getUrl } from "../utils/OptumPrograms";
import { useApplicationContext, ApplicationContainerAPI } from "../context/ApplicationContext";
import { performSignOut } from "../security/signOut";
import { createLogger } from '../utils/logging';
import { isNoiseMessage } from '../utils/postMessageFilter';

const logger = createLogger('NavigationMessaging');

type NavigationMessage = {
  message: string;
  programCode: string;
  jumpParams: string;
  parameters?: any;
  url?: string;
  tenant?: string;
  mediatorId?: string;
};

export default function useNavigationMessaging() {
  const ctx = useApplicationContext();
  const ctxRef = useRef<ApplicationContainerAPI>(ctx);
  // keep latest container accessible to the handler without re-registering the listener
  ctxRef.current = ctx;

  const safeStringify = (v: any, max = 1000) => {
    try {
      const s = typeof v === "string" ? v : JSON.stringify(v);
      return s.length > max ? s.substring(0, max) + "... (truncated)" : s;
    } catch (e) {
      try {
        return String(v);
      } catch (e2) {
        return "[unserializable]";
      }
    }
  };

  const describeEvent = (e: MessageEvent) => {
    if (!e || typeof e.data === "undefined") return "<no-data>";
    if (e.data && typeof e.data === "object" && (e.data.message || e.data.type)) {
      return String(e.data.message || e.data.type);
    }
    if (typeof e.data === "string") return e.data.slice(0, 120);
    return Object.prototype.toString.call(e.data);
  };

  const formatProgramNameForRxClaimWeb = (programName: string): string => {
    const lastCharacter = programName[programName.length - 1];
    if (lastCharacter !== "I" && /[a-z]/i.test(lastCharacter)) {
      return programName.substring(0, programName.length - 1);
    }
    return programName;
  };

  const isIframeElement = (input: HTMLElement | null): input is HTMLIFrameElement =>
    input !== null && input.tagName === "IFRAME";

  const postToFrame = (message: any): boolean => {
    const frame = document.getElementById("frame0");
    if (isIframeElement(frame) && frame.contentWindow) {
      frame.contentWindow.postMessage(message, "*");
      return true;
    }
    return false;
  };

  const isEligibilityFrameMessage = (event: MessageEvent): boolean => {
    const frame = document.getElementById("frame0");
    return isIframeElement(frame)
      && frame.src.includes("rxclaim-nextgen-eligibility")
      && event.source === frame.contentWindow;
  };

  const openPresto = (
    env: any,
    tenantId: string,
    currentProgram: string,
    prestoURLParams: string,
  ) => {
    let authType = getAuthType();
    if (authType === "testing") {
      authType = "msid";
    }

    let urlParms = "";
    if (prestoURLParams) {
      window.sharedParameters = prestoURLParams;
      urlParms = "&pgm=" + currentProgram;
    }

    try {
      const openURL = env.tenant[tenantId][authType].presto + "?tenant=" + tenantId + urlParms;
      const prestoWindow = window.open(openURL, "RxClaimWeb");
      ctxRef.current.setPrestoWindow(prestoWindow);
    } catch (error) {
      logger.error('Presto Open Failed', 'Unable to open RxClaim Web window', { error, tenantId, currentProgram });
    }
  };

  const handler = (event: MessageEvent) => {
    const ctx = ctxRef.current;

    if (isNoiseMessage(event)) return;

    try {
      logger.debug('PostMessage Received', `${describeEvent(event)}`, {
        origin: event.origin,
        dataPreview: safeStringify(event.data, 500),
      });
    } catch (e) {
      // ignore logging failures
    }

    const {
      navigateToSection,
      setProgramData,
      setCurrentProgram,
      updateTenant,
      pushTenantStack,
      popTenantStack,
      setCurrentTenant,
      setHome,
    } = ctx;

    if (event.data === "close-iframe") {
      logger.info('Close Iframe', 'Hiding provider iframe and returning home');
      const previousIframe = document.getElementById("iframe-provider");
      if (previousIframe) previousIframe.style.display = "none";
      setHome();
      return;
    }

    if (event.data === "logout") {
      logger.info('Logout Requested', 'Signing out and redirecting');
      void performSignOut();
      return;
    }

    if (event.data === "presto-signout") {
      logger.info('Presto Signout', 'Closing RxClaim Web window');
      ctx.prestoWindow?.close?.();
      return;
    }

    if (event.data === "is-unified-ui-online") {
      logger.debug('Heartbeat Check', 'Replying to Presto online check');
      ctx.prestoWindow?.postMessage?.({ event: "is-unified-ui-online", response: "active", program: "" }, "*");
      return;
    }

    if (event.data === "request-pgm-parameters") {
      logger.debug('Parameters Request', 'Forwarding shared parameters to Presto');
      ctx.prestoWindow?.postMessage?.({ event: "setProgramJumpParameters", parameters: window.sharedParameters }, "*");
      return;
    }

    if (event.data?.message === "updateFrame") {
      logger.info('Update Frame', 'Navigating to URL', { url: event.data.url });
      navigateToSection(event.data.url, false);
      return;
    }

    if (event.data?.message === "updateFrameFromChild") {
      logger.info('Update Frame From Child', 'Child iframe navigation', { url: event.data.url });
      navigateToSection(event.data.url, false);
      return;
    }

    const navigationMessage = event.data as NavigationMessage;

    if (navigationMessage?.message === "react-page-loaded") {
      logger.debug('React Page Loaded', 'Checking for pending program data', { hasProgramData: !!ctx.programData });
      // Not cleared: RenderIframe's onLoad and the load-program-data handler below can also
      // consume programData for the same navigation, and whichever fires last would otherwise
      // see it already nulled out and send an empty object.
      if (ctx.programData) {
        postToFrame({ type: "set-params", data: ctx.programData });
      }
      return;
    }

    // Pull-based replacement for the "react-page-loaded" push above, used by GenAI -> GenAI
    // external lookups: those render as an iframe nested inside frame0 (not a direct child of
    // this document), so there's no element to grab via getElementById("frame0"). The requester
    // must post this directly to window.top (not window.parent) so it reaches us regardless of
    // nesting depth, and we reply to event.source so the response always targets whichever
    // window actually asked - frame0 or a deeply nested lookup - instead of guessing.
    if (navigationMessage?.message === "load-program-data") {
      logger.debug('Load Program Data', 'Replying with program data to requesting frame', { hasProgramData: !!ctx.programData });
      const requester = event.source as Window | null;
      // Downstream consumers index/spread `data`
      // without null checks, so only reply when there's actual program data to send.
      if (requester?.postMessage && ctx.programData) {
        logger.debug('Set Params', 'Posting program data to requesting frame', { data: ctx.programData });
        requester.postMessage({ type: "set-params", data: ctx.programData }, "*");
      }
      return;
    }

    if (navigationMessage?.message === "navigate-to-external-program") {
      logger.info('External Program Navigation', 'Navigating to external program', {
        url: navigationMessage.url,
        hasParameters: !!navigationMessage.parameters,
      });
      const programPayload = navigationMessage?.parameters?.state;
      if (navigationMessage.url) {
        navigateToSection(navigationMessage.url, false, programPayload, true);
      } else if (programPayload) {
        // fallback: if no url provided, just set the program data
        setProgramData(programPayload);
      }
      return;
    }

    if (navigationMessage?.message === "navigate-to-ai-program") {
      logger.info('AI Program Navigation', 'Navigating to AI program', {
        programCode: navigationMessage.programCode,
        mediatorId: navigationMessage.mediatorId,
      });
      const url = getAIProgramUrl(navigationMessage.programCode, navigationMessage.parameters, navigationMessage.mediatorId);
      navigateToSection(url, false);
      return;
    }

    if (navigationMessage?.message === "new-navigate-to-ai-program") {
      logger.info('AI Program Navigation', 'Navigating to AI program', {
        programCode: navigationMessage.programCode,
        mediatorId: navigationMessage.mediatorId,
      });
      const url = getUrl(navigationMessage.programCode);
      navigateToSection(url, false);

      setTimeout(() => {
        const frame = document.getElementById("frame0") as HTMLIFrameElement | null;
        logger.info('AI Program Parameters', 'Verifying parameters before sending to target program', {
          programCode: navigationMessage.programCode,
          mediatorId: navigationMessage.mediatorId,
          parameters: safeStringify(navigationMessage.parameters),
        });
        frame?.contentWindow?.postMessage(
          {
            message: "navigate-to-ai-new-program",
            programCode: navigationMessage.programCode,
            parameters: navigationMessage.parameters,
          },
          "*"
        );
      }, 2000);
      return;
    }

    if (navigationMessage?.message === "navigate-to-program") {
      const programPayload = navigationMessage?.jumpParams;
      // Callers may omit legacyProgramCode on the jump entry; the target Fresche
      // slice needs it on state[len - 2] to build its back navigation.
      const jumpState = (programPayload as any)?.state;
      if (navigationMessage.programCode && Array.isArray(jumpState) && jumpState.length > 1) {
        const jumpEntry = jumpState[jumpState.length - 2];
        if (jumpEntry && !jumpEntry.legacyProgramCode) {
          jumpEntry.legacyProgramCode = navigationMessage.programCode;
        }
      }
      logger.info('Program Navigation', 'Navigating to program', { programCode: navigationMessage.programCode });
      if (navigationMessage.programCode) {
        window.name = "UI-parent";
        setCurrentProgram(navigationMessage.programCode);
        const tenantForProgram = ctx.currentTenant || ctx.tenantId;
        navigateToSection(
          getProgramUrl(navigationMessage.programCode, navigationMessage.parameters, tenantForProgram),
          false,
          programPayload,
        );
      } else if (programPayload) {
        // fallback: ensure program data is set even if no programCode/url provided
        setProgramData(programPayload);
      }
      return;
    }

    if (navigationMessage?.message === "navigate-to-program-internal") {
      logger.info('Internal Program Navigation', 'Navigating to internal program', { programCode: navigationMessage.programCode });
      if (navigationMessage.programCode) {
        navigateToSection(getProgramUrl(navigationMessage.programCode, navigationMessage.parameters, ctx.tenantId), false);
      }
      return;
    }

    if (navigationMessage?.message === "navigate-to-presto") {
      logger.info('Navigate to Presto', 'Opening RxClaim Web (Presto)', { programCode: navigationMessage.programCode });
      const environment = (window as any)["environments"];
      const programName = formatProgramNameForRxClaimWeb(navigationMessage.programCode);
      setCurrentProgram(programName);

      const pgmParams = navigationMessage.parameters || {};
      const paramData = window.pgmParameters?.[programName];
      if (!paramData) {
        logger.error('Program Parameters', 'Missing program parameter information', { programName });
        return;
      }

      const errors: string[] = [];
      paramData.parameters.forEach((parameter: any) => {
        if (parameter.usage && parameter.usage === "O") parameter.value = "";
        else if (typeof pgmParams[parameter.field] === "undefined") {
          // ignore
        } else parameter.value = pgmParams[parameter.field];
      });

      if (errors.length !== 0) {
        logger.error('Program Parameters', 'Program parameter information mismatch', { programName, errors });
        return;
      }

      const rxclaimWebParameters = RxClaimWebTools.formatWebParameters(paramData.parameters);
      openPresto(environment, ctx.tenantId, programName, rxclaimWebParameters);
      logger.info('Presto Opened', 'Successfully opened RxClaim Web', { programName, tenantId: ctx.tenantId });
      return;
    }

    if (navigationMessage?.message === "getTenantId") {
      logger.debug('Get Tenant ID', 'Posting tenant ID to iframe', { tenantId: ctx.tenantId });
      postToFrame({ message: "getTenantId", tenantId: ctx.tenantId });
      return;
    }

    if (navigationMessage?.message === "getSessionInfo") {
      logger.debug('Get Session Info', 'Sending session info to iframe');
      const sessionId = getSessionId();
      const storedAuthType = getAuthType();
      const authType = storedAuthType === "testing" ? "msid" : storedAuthType;
      const iframeSessionId = isEligibilityFrameMessage(event)
        ? sessionId.replace(/-/g, "+").replace(/_/g, "/")
        : sessionId;
      postToFrame({ message: "sendSessionInfo", session_id: iframeSessionId, idp: authType, tenantId: ctx.tenantId });
      return;
    }

    if (navigationMessage?.message === "changeTenant") {
      logger.info('Change Tenant', 'Switching to different tenant', { tenant: navigationMessage.tenant });
      const tenant = navigationMessage.tenant;
      if (tenant) {
        setCurrentTenant(ctx.tenantId);
        updateTenant(tenant);
        pushTenantStack(tenant);
      }
      return;
    }

    if (navigationMessage?.message === "restoreTenant") {
      logger.info('Restore Tenant', 'Popping tenant stack to restore previous tenant');
      const stackItems = ctx.tenantStack;
      if (stackItems.length > 1) {
        const newTop = stackItems[stackItems.length - 2];
        popTenantStack();
        updateTenant(newTop);
        setCurrentTenant(newTop);
      }
      return;
    }

    if (navigationMessage?.message === "getAbilities") {
      logger.debug('Get Abilities', 'Sending abilities to iframe');
      postToFrame({ message: "sendAbilities", abilities: ctx.abilities });
      return;
    }

    if (navigationMessage?.message === "sendProgramName") {
      // Save latest help context and delegate opening to context helper
      try {
        ctxRef.current.setLatestHelp?.({
          application: event.data.application,
          programName: event.data.programName,
          isPriceFrame: ctxRef.current.isPriceFrame,
        });
        logger.debug('Send Program Name', 'Saving help context and opening help', { application: event.data.application, programName: event.data.programName, isPriceFrame: ctxRef.current.isPriceFrame });
      } catch (e) {
        // ignore
      }
      ctxRef.current.openHelp?.();
      return;
    }

    if (navigationMessage?.message === "sendRxClaimLite") {
      const screen = event.data.helpPage;
      try {
        ctxRef.current.setLatestHelp?.({ helpPage: screen });
        logger.debug('Send RxClaim Lite', 'Opening RxClaim Lite help page', { helpPage: screen });
      } catch (e) {
        // ignore
      }
      ctxRef.current.openHelp?.();
    }
  };

  // Register message handler once on mount. Handler reads fresh values from ctxRef.current
  // on every invocation, so it doesn't need to be re-registered when dependencies change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);
}
